import { Modal, Carousel, Spin } from 'antd';
import type { CarouselRef } from 'antd/es/carousel';
import { ExtensionStep } from '../../../../interfaces/Extension';
import {
  SetStateAction,
  Dispatch,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import { ContextApp } from '../../../../contexts/ContextApp';
import GraphqlService from '../../../../services/graphql/GraphqlService';
import { EnumsValues } from '../../../../enums/EnumsValues';
import { CloseCircleFilled } from '@ant-design/icons';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import ExtensionWrapper from '../../../../components/common/ExtensionWrapper/ExtensionWrapper';
import CurrentReceiptInformation from './CurrentReceiptInformation';
import './ExtensionModal.less';
import { ExtensionWithReceiptHeader } from '../../../../interfaces/Extension';
import PreintegrationCompleteMessage from './PreintegrationCompleteMessage';

interface Props {
  isModalOpen: boolean;
  onModalClose: () => void;
  step: ExtensionStep;
  resourceId: number;
  initialReceiptList: ExtensionWithReceiptHeader[] | undefined;
  tenant_id: number | undefined;
  setInitialReceiptList: Dispatch<
    SetStateAction<ExtensionWithReceiptHeader[] | undefined>
  >;
}

const ExtensionModal = ({
  isModalOpen,
  onModalClose,
  step,
  resourceId,
  initialReceiptList,
  setInitialReceiptList,
  tenant_id,
}: Props) => {
  const { t } = useContext(ContextApp);
  const { Query, customRequest } = GraphqlService();
  const [currentIndex, setCurrentIndex] =
    useState<number | undefined>(undefined);
  const [completedReceiptsState, setCompletedReceiptsState] = useState<{
    [key: string]: boolean;
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoAdvancedReceipts, setAutoAdvancedReceipts] = useState<Set<string>>(
    new Set(),
  );
  const carouselRef = useRef<CarouselRef>(null);

  const totalReceiptsAmount = initialReceiptList?.length;
  const totalCompletedReceiptsAmount = Object.values(
    completedReceiptsState,
  ).filter((completed) => completed).length;

  const progress = `${totalCompletedReceiptsAmount}/${totalReceiptsAmount} `;
  const flagHiddenProgress = true; //TODO: Se agrega esta bandera para no perder el desarrollo y ver de reutilizarlo de alguna forma más adecuada
  const modalTitle =
    (!flagHiddenProgress ? progress : '') +
    t('extensionPhase.ModalPostProcessedTitle');

  useEffect(() => {
    if (isModalOpen && resourceId && initialReceiptList) {
      const index = initialReceiptList.findIndex(
        (receipt) =>
          receipt.receipt_header_id === resourceId &&
          step.id === receipt.extension_step_id,
      );

      if (index !== -1) setCurrentIndex(index);
    }
    return () => {
      setCurrentIndex(undefined);
    };
  }, [isModalOpen, resourceId, initialReceiptList]);

  useEffect(() => {
    if (isModalOpen && initialReceiptList) {
      const newState: { [key: string]: boolean } = {
        ...completedReceiptsState,
      };

      initialReceiptList.forEach((receipt) => {
        if (
          !(
            `${receipt.receipt_header?.id}-${receipt.extension_step_id}` in
            newState
          )
        ) {
          newState[
            `${receipt.receipt_header?.id!}-${receipt.extension_step_id}`
          ] = false;
        }
      });
      setCompletedReceiptsState(newState);
    }

    return () => {
      setCompletedReceiptsState({});
    };
  }, [isModalOpen, initialReceiptList]);

  const handleUpdateReceiptList = async (fromRetry: boolean = false) => {
    try {
      const oneMissing =
        totalReceiptsAmount === EnumsValues.ConstNumbers.one ||
        totalReceiptsAmount! - totalCompletedReceiptsAmount ===
          EnumsValues.ConstNumbers.one;

      if (oneMissing && !fromRetry && initialReceiptList!.length > 0) {
        checkIfLastReceiptNeedsUpdate();
        return;
      }

      const data: ExtensionWithReceiptHeader[] = await customRequest({
        query: Query.receiptHeaderExtensionSteps,
        variables: {
          tenant_id,
          filter: { front_intervention: true },
        },
      });

      const { completedReceipts, newReceipts } = getCompletedAndNewReceipts(
        initialReceiptList!,
        data,
      );

      // 3.actualiza el estado cuando viene actualizado por otro user
      if (completedReceipts.length > 0) {
        completedReceipts.forEach((completedReceipt) => {
          const receiptId = completedReceipt.receipt_header?.id;
          const key = `${receiptId}-${completedReceipt.extension_step_id}`;

          if (receiptId && completedReceiptsState[key] === false) {
            setCompletedReceiptsState((prevState) => ({
              ...prevState,
              [key]: true,
            }));
          }
        });
      }

      if (newReceipts.length > 0) {
        setInitialReceiptList((prevList: ExtensionWithReceiptHeader[] = []) => [
          ...prevList,
          ...newReceipts,
        ]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getCompletedAndNewReceipts = (
    initialList: ExtensionWithReceiptHeader[],
    updatedList: ExtensionWithReceiptHeader[],
  ) => {
    const completedReceipts = initialList.filter((receipt) =>
      updatedList.every(
        (updatedReceipt) =>
          updatedReceipt.receipt_header?.id !== receipt.receipt_header?.id ||
          updatedReceipt.extension_step_id !== receipt.extension_step_id,
      ),
    );

    const newReceipts = updatedList.filter((updatedReceipt) =>
      initialList.every(
        (receipt) =>
          receipt.receipt_header?.id !== updatedReceipt.receipt_header?.id ||
          receipt.extension_step_id !== updatedReceipt.extension_step_id,
      ),
    );

    return { completedReceipts, newReceipts };
  };

  //callback para actualizar el estado desde finnegansanexoydimensiones
  const handleUpdateCompletedStatus = (
    resourceId: number,
    receiptCurrentStep: number,
  ) => {
    const key = `${resourceId}-${receiptCurrentStep}`;
    setCompletedReceiptsState((prevState) => ({
      ...prevState,
      [key]: true,
    }));
  };

  const checkIfLastReceiptNeedsUpdate = async () => {
    setIsLoading(true);

    await new Promise((resolve) => setTimeout(resolve, 5000));

    await handleUpdateReceiptList(true);

    setIsLoading(false);
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={() => {
        setAutoAdvancedReceipts(new Set());
        setInitialReceiptList([]);
        onModalClose();
      }}
      title={modalTitle}
      className="modal-view-receipt"
      width={800}
      bodyStyle={{
        height: '75vh',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
      footer={null}
      style={{ overflow: 'hidden' }}
      key={step?.code}
    >
      {currentIndex !== undefined && currentIndex !== -1 ? (
        <Carousel
          ref={carouselRef}
          infinite={true}
          dots={false}
          draggable
          initialSlide={currentIndex}
          afterChange={(index) => setCurrentIndex(index)}
          lazyLoad="ondemand"
        >
          {initialReceiptList?.map((receipt) => {
            const currencyType = receipt.receipt_header?.currency_type;
            const integrationName = t(
              receipt.extension_step.extension_phase?.translation_key as never,
              {
                defaultValue: receipt.extension_step.extension_phase?.name,
              },
            );

            if (currencyType) {
              currencyType.name =
                currencyType.id === EnumsValues.ConstNumbers.two
                  ? 'USD'
                  : 'ARS';
            }

            const completedCurrentReceiptState =
              completedReceiptsState[
                `${receipt.receipt_header?.id}-${receipt.extension_step_id}`
              ];

            return (
              <div key={receipt.receipt_header?.id}>
                <CurrentReceiptInformation
                  sender={receipt.receipt_header?.sender?.name}
                  receiptnumber={receipt.receipt_header?.receipt_number}
                  amount={receipt.receipt_header?.receipt_total}
                  currency={receipt.receipt_header?.currency_type?.name}
                />

                {isLoading ? (
                  <div className="spin">
                    <Spin size="large" />
                  </div>
                ) : completedCurrentReceiptState ? (
                  (() => {
                    const receiptId = `${receipt.receipt_header?.id}-${receipt.extension_step_id}`;
                    if (!autoAdvancedReceipts.has(receiptId)) {
                      const nextIndex = initialReceiptList.findIndex(
                        (receipt) => {
                          const id = `${receipt.receipt_header?.id}-${receipt.extension_step_id}`;
                          return (
                            !completedReceiptsState[id] &&
                            !autoAdvancedReceipts.has(id)
                          );
                        },
                      );

                      const hayPendiente = nextIndex !== -1;

                      if (hayPendiente) {
                        setTimeout(() => {
                          carouselRef?.current?.goTo(nextIndex);
                          setAutoAdvancedReceipts((prev) =>
                            new Set(prev).add(receiptId),
                          );
                        }, 1500);
                      } else {
                        setAutoAdvancedReceipts((prev) =>
                          new Set(prev).add(receiptId),
                        );
                      }
                    }

                    return (
                      <PreintegrationCompleteMessage
                        integrationName={integrationName}
                      />
                    );
                  })()
                ) : (
                  <ErrorBoundary
                    fallbackRender={(props) => <FallbackRender {...props} />}
                  >
                    <ExtensionWrapper
                      step={receipt.extension_step}
                      resourceId={receipt.receipt_header?.id!}
                      validateReceipts={handleUpdateReceiptList}
                      setIsCompleted={handleUpdateCompletedStatus}
                    />
                  </ErrorBoundary>
                )}
              </div>
            );
          })}
        </Carousel>
      ) : (
        <div className="spin">
          <Spin size="large" />
        </div>
      )}
    </Modal>
  );
};

export default ExtensionModal;

const FallbackRender = (_props: FallbackProps) => {
  const [whatsappContactLink, setWhatsappContactLink] = useState('');
  const [supportContactLink, setSupportContactLink] = useState('');
  const { customRequest, Query } = GraphqlService();

  const getContactLinks = () => {
    customRequest({
      query: Query.getAppSettingByKey,
      variables: {
        input: {
          key: EnumsValues.SettingNames.supportEmail,
        },
      },
    })
      .then((response: any) => {
        setSupportContactLink(response.setting_value);
      })
      .catch(console.error);

    customRequest({
      query: Query.getAppSettingByKey,
      variables: {
        input: {
          key: EnumsValues.SettingNames.ContactLinkOnHelp,
        },
      },
    })
      .then((response: any) => {
        setWhatsappContactLink(response.setting_value);
      })
      .catch(console.error);
  };

  useEffect(() => {
    getContactLinks();
  }, []);

  return (
    <div role="alert">
      <CloseCircleFilled
        style={{ color: 'red', fontSize: 32, marginBottom: 12 }}
      />
      <h3 style={{ marginBottom: '1rem' }}>Algo salio mal...</h3>
      <p>
        Estamos trabajando para solucionarlo. Vuelve a intentarlo en unos
        minutos. Si el problema persiste, contactanos a través de{' '}
        <a href={whatsappContactLink}>WhatsApp</a>, por correo electrónico a{' '}
        <a href={`mailto:${supportContactLink}`}>{supportContactLink}</a>.
      </p>
    </div>
  );
};
